import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Sparkles, BookOpen, Loader as Loader2, Bookmark } from 'lucide-react';
import { useApi } from '../../../lib/useApi';
import type { CraftModuleRecord, CraftModuleTabKey, UserCraftProgress } from '../../../types/craft';
import { CRAFT_MODULE_TABS } from '../../../types/craft';
import CoreConceptTab from './tabs/CoreConceptTab';
import WhyItMattersTab from './tabs/WhyItMattersTab';
import WhatGoodLooksLikeTab from './tabs/WhatGoodLooksLikeTab';
import CommonMistakesTab from './tabs/CommonMistakesTab';
import RealWorldApplicationTab from './tabs/RealWorldApplicationTab';
import RoleSegmentAdaptationTab from './tabs/RoleSegmentAdaptationTab';
import FrameworksTalkTracksTab from './tabs/FrameworksTalkTracksTab';
import PracticeReinforcementTab from './tabs/PracticeReinforcementTab';
import MetricsOutcomesTab from './tabs/MetricsOutcomesTab';
import EvolutionUpdatesTab from './tabs/EvolutionUpdatesTab';

interface Props {
  moduleSlug: string;
  progress: UserCraftProgress[];
  onBack: () => void;
  onProgressUpdate: () => void;
}

const TAB_FIELD_MAP: Record<CraftModuleTabKey, keyof CraftModuleRecord> = {
  core_concept: 'tab_core_concept',
  why_it_matters: 'tab_why_it_matters',
  what_good_looks_like: 'tab_what_good_looks_like',
  common_mistakes: 'tab_common_mistakes',
  real_world_application: 'tab_real_world_application',
  role_segment_adaptation: 'tab_role_segment_adaptation',
  frameworks_talk_tracks: 'tab_frameworks_talk_tracks',
  practice_reinforcement: 'tab_practice_reinforcement',
  metrics_outcomes: 'tab_metrics_outcomes',
  evolution_updates: 'tab_evolution_updates',
};

export default function CraftModuleViewer({ moduleSlug, progress, onBack, onProgressUpdate }: Props) {
  const { apiGet, apiPost } = useApi();
  const [module, setModule] = useState<CraftModuleRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<CraftModuleTabKey>('core_concept');
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { module: data } = await apiGet<{ module: CraftModuleRecord | null }>(
          `/v1/craft-modules/${moduleSlug}`,
        );
        if (data) setModule(data);
      } catch {
        // leave module null → renders the "Module not found" state
      }
      setLoading(false);
    })();
  }, [moduleSlug, apiGet]);

  const checkScroll = () => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    checkScroll();
    const el = tabsRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [loading]);

  const scrollTabs = (dir: 'left' | 'right') => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const moduleProgress = progress.filter(p => p.module_slug === moduleSlug);

  const markTabComplete = async (tabKey: CraftModuleTabKey) => {
    if (!module) return;
    const existing = moduleProgress.find(p => p.article_slug === tabKey);
    if (existing?.status === 'completed') return;

    await apiPost('/v1/user-craft-progress', {
      module_slug: moduleSlug,
      article_slug: tabKey,
      status: 'completed',
    });
    onProgressUpdate();
  };

  const handleTabChange = (key: CraftModuleTabKey) => {
    if (activeTab !== key) {
      markTabComplete(activeTab);
    }
    setActiveTab(key);
  };

  const getTabStatus = (key: CraftModuleTabKey) => {
    return moduleProgress.find(p => p.article_slug === key)?.status || 'not_started';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-app-muted animate-spin" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="text-center py-20">
        <p className="text-sm text-app-muted">Module not found.</p>
        <button onClick={onBack} className="mt-3 text-sm text-forest-600 hover:text-forest-800">Go back</button>
      </div>
    );
  }

  const completedTabs = moduleProgress.filter(p => p.status === 'completed').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { markTabComplete(activeTab); onBack(); }}
            className="flex items-center gap-1 text-sm text-app-muted hover:text-app-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="w-px h-5 bg-app-line" />
          <div>
            <h2 className="text-lg font-bold text-app-ink">{module.title}</h2>
            <div className="flex items-center gap-3 text-xs text-app-muted mt-0.5">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {module.estimated_duration_minutes}m
              </span>
              {module.quarter_label && (
                <span className="flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {module.quarter_label}
                </span>
              )}
              <span className="flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                {completedTabs}/10 completed
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative mb-6">
        {canScrollLeft && (
          <button
            onClick={() => scrollTabs('left')}
            className="absolute left-0 top-0 bottom-0 z-10 w-8 flex items-center justify-center bg-app-surface"
          >
            <ChevronLeft className="w-4 h-4 text-app-muted" />
          </button>
        )}

        <div
          ref={tabsRef}
          className="flex gap-1 overflow-x-auto scrollbar-hide px-1 pb-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {CRAFT_MODULE_TABS.map(tab => {
            const isActive = activeTab === tab.key;
            const status = getTabStatus(tab.key);
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg whitespace-nowrap transition flex-shrink-0 ${
                  isActive
                    ? 'bg-forest-50 text-forest-700 border border-forest-200 shadow-none'
                    : 'text-app-muted hover:text-app-ink hover:bg-app-canvas border border-transparent'
                }`}
              >
                {status === 'completed' && (
                  <span className="w-1.5 h-1.5 rounded-full bg-app-positive flex-shrink-0" />
                )}
                {tab.shortLabel}
              </button>
            );
          })}
        </div>

        {canScrollRight && (
          <button
            onClick={() => scrollTabs('right')}
            className="absolute right-0 top-0 bottom-0 z-10 w-8 flex items-center justify-center bg-app-surface"
          >
            <ChevronRight className="w-4 h-4 text-app-muted" />
          </button>
        )}
      </div>

      <div className="min-h-[400px]">
        {activeTab === 'core_concept' && <CoreConceptTab data={module.tab_core_concept} />}
        {activeTab === 'why_it_matters' && <WhyItMattersTab data={module.tab_why_it_matters} />}
        {activeTab === 'what_good_looks_like' && <WhatGoodLooksLikeTab data={module.tab_what_good_looks_like} />}
        {activeTab === 'common_mistakes' && <CommonMistakesTab data={module.tab_common_mistakes} />}
        {activeTab === 'real_world_application' && <RealWorldApplicationTab data={module.tab_real_world_application} />}
        {activeTab === 'role_segment_adaptation' && <RoleSegmentAdaptationTab data={module.tab_role_segment_adaptation} />}
        {activeTab === 'frameworks_talk_tracks' && <FrameworksTalkTracksTab data={module.tab_frameworks_talk_tracks} />}
        {activeTab === 'practice_reinforcement' && <PracticeReinforcementTab data={module.tab_practice_reinforcement} />}
        {activeTab === 'metrics_outcomes' && <MetricsOutcomesTab data={module.tab_metrics_outcomes} />}
        {activeTab === 'evolution_updates' && (
          <EvolutionUpdatesTab
            data={module.tab_evolution_updates}
            quarterLabel={module.quarter_label}
            version={module.version}
          />
        )}
      </div>
    </div>
  );
}
